import { resolveInferenceEngine } from "../../../../../../../../src/shared/resolveInferenceEngine";
import { useEffect, useState } from "react";
import { get } from "lodash";
import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useFormikContext, Field, ErrorMessage } from "formik";
import { getLiquidReferencedVariables } from "../../../../../../../../src/shared/schemas/PluginSchema";
import IconButton from "src/components/IconButton";
import classNames from "classnames";
import { useWorkflowStore } from "../../workflowStore";
import { getAvailableVariables } from "../utils";

const MAX_NUM_GENERATIONS = 3;

export const InferenceBlockEditor = ({ block, number, hasFieldOverride, blockIndex, optionsPath, selectedOverrideId, onOverrideSelected }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const availableEngines = useWorkflowStore((state) => state.availableEngines);
  const { values, errors, setFieldValue } = useFormikContext();
  const inferenceBlocks = values.blocks.filter((b) => b.blockType === "inference");
  const hasLaterInferenceBlocks = values.blocks.filter((b, i) => b.blockType === "inference" && i > blockIndex).length > 0;

  const hasOptionsError = !!get(errors, optionsPath);
  const hasPromptError = !!get(errors, `${optionsPath}.prompt`);
  const fieldOptionNameFromBase = (optionName) => `${optionsPath}.${optionName}`;

  const currentEngine = resolveInferenceEngine(get(values, fieldOptionNameFromBase("engine")));

  const formattedEngineOptions = Object.entries(availableEngines)
    .filter(([engine]) => resolveInferenceEngine(engine) === engine)
    .map(([key, value]) => ({
      value: key,
      label: key,
      description: value.description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // If current N is set to more than the max the engine can handle, then set to the engine's max
  useEffect(() => {
    const max = availableEngines[currentEngine]?.maxN ? availableEngines[currentEngine].maxN : MAX_NUM_GENERATIONS;
    const currentNumGenerations = get(values, fieldOptionNameFromBase("numGenerations"));

    // Only validate static values, not variables
    if (typeof currentNumGenerations === "number" && currentNumGenerations > max) {
      setFieldValue(fieldOptionNameFromBase("numGenerations"), max);
    }
  }, [currentEngine]);

  // Ensure max tokens is not greater than the changed engines engine's max tokens
  useEffect(() => {
    const max = availableEngines[currentEngine]?.maxTokens || 4096; // use smallest max if not defined

    if (get(values, fieldOptionNameFromBase("maxTokens")) > max) {
      setFieldValue(fieldOptionNameFromBase("maxTokens"), max);
    }
  }, [currentEngine]);

  // Generate options for N
  const n = availableEngines[currentEngine]?.maxN ? availableEngines[currentEngine].maxN : MAX_NUM_GENERATIONS;

  const numGenerationOptions = Array.from({ length: n }, (_, i) => ({ label: i + 1, value: i + 1 }));

  const availableVariables = getAvailableVariables(values.blocks, block.id);

  const usedVariablesSet = block.options.prompt.messages.reduce((acc, message) => {
    const messageVariables = getLiquidReferencedVariables(message.template);
    return new Set([...acc, ...messageVariables]);
  }, new Set());

  const usedVariables = Array.from(usedVariablesSet);
  const possibleMistakes = usedVariables.filter((variable) => !availableVariables.includes(variable));

  return (
    <>
      <div className="mb-4">
        <h4>Available Variables</h4>
        <div className="flex flex-wrap gap-2 items-center min-h-10">
          {availableVariables.map((variable) => (
            <div
              key={variable}
              className={`${usedVariables.includes(variable) ? "text-green-500" : ""}`}
            >
              {variable}
            </div>
          ))}

          {possibleMistakes.map((mistake) => (
            <div
              key={mistake}
              className="text-red-500"
            >
              {mistake}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div>
          {block.options.prompt.messages.map((message, messageIndex) => {
            const fieldName = `${optionsPath}.prompt.messages[${messageIndex}].template`;
            if (message.role === "system") {
              return (
                <label
                  key={fieldName}
                  className="block mb-4"
                >
                  <h3 className="text-lg font-semibold mb-2">System</h3>
                  <Field name={fieldName}>
                    {({ field, form }) => (
                      <Textarea
                        placeholder={`Read the {{highlighted_text}} and then output the kind of reaction that a reader would have when they read this text.`}
                        className={`w-full p-2 border rounded ${
                          hasFieldOverride(`options.prompt.messages.${messageIndex}.template`) ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"
                        }`}
                        id="prompt"
                        minRows={3}
                        {...field}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name={fieldName}
                    component="p"
                    className="text-red-500 text-sm mt-1"
                  />
                </label>
              );
            } else {
              return (
                <div
                  key={fieldName}
                  className="block mb-4"
                >
                  <div className="flex items-center gap-2">
                    <Field name={`${optionsPath}.prompt.messages[${messageIndex}].role`}>
                      {({ field }) => (
                        <select
                          {...field}
                          className={`p-2 border rounded ${hasFieldOverride(`options.prompt.messages.${messageIndex}.role`) ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
                        >
                          <option value="user">User</option>
                          <option value="assistant">Assistant</option>
                        </select>
                      )}
                    </Field>
                    {messageIndex > 1 && (
                      <SudoButton
                        variant="tertiary"
                        className="mt-0 p-0"
                        onClick={(e) => {
                          e.preventDefault();
                          const newMessages = [...block.options.prompt.messages.filter((_, i) => i !== messageIndex)];
                          setFieldValue(`${optionsPath}.prompt.messages`, newMessages);
                        }}
                      >
                        remove
                      </SudoButton>
                    )}
                  </div>
                  <Field name={fieldName}>
                    {({ field }) => (
                      <Textarea
                        placeholder={`Read the {{highlighted_text}} and then output the kind of reaction that a reader would have when they read this text.`}
                        className={`w-full p-2 border rounded ${
                          hasFieldOverride(`options.prompt.messages.${messageIndex}.template`) ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"
                        }`}
                        id="prompt"
                        {...field}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name={fieldName}
                    component="p"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              );
            }
          })}
        </div>
        <SudoButton
          variant="tertiary"
          className="mt-0 p-0"
          onClick={(e) => {
            e.preventDefault();
            setFieldValue(`${optionsPath}.prompt.messages`, [...block.options.prompt.messages, { role: "user", template: "" }]);
          }}
        >
          Add Message
        </SudoButton>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <IconButton
          className={classNames("transition-transform", {
            "rotate-180": settingsOpen,
          })}
          onClick={(e) => {
            e.preventDefault();
            setSettingsOpen(!settingsOpen);
          }}
          src="../../../../assets/settings_dropdown_icon.svg"
        />
        <h3 className="text-lg font-semibold">AI Options</h3>
      </div>
      {settingsOpen && (
        <>
          <label className="block mb-4">
            <h3 className="text-lg font-semibold mb-2">Engine</h3>
            <Field name={fieldOptionNameFromBase("engine")}>
              {({ field, form }) => {
                return (
                  <>
                    <select
                      id="model"
                      onChange={(e) => {
                        form.setFieldValue(field.name, e.target.value);
                      }}
                      value={currentEngine}
                      className={`w-full p-2 border rounded ${hasFieldOverride("options.engine") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
                    >
                      {formattedEngineOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label} - {option.description}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage
                      name={fieldOptionNameFromBase("engine")}
                      component="p"
                      className="text-red-500 text-sm mt-1"
                    />
                  </>
                );
              }}
            </Field>
          </label>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <label className="block">
              <h3 className="text-lg font-semibold mb-2">Temperature</h3>
              <Field
                name={fieldOptionNameFromBase("temperature")}
                type="number"
                component="input"
                className={`p-2 border rounded ${hasFieldOverride("options.temperature") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
              <ErrorMessage
                name={fieldOptionNameFromBase("temperature")}
                component="p"
                className="text-red-500 text-sm mt-1"
              />
            </label>
            <label className="block">
              <h3 className="text-lg font-semibold mb-2">Frequency Penalty</h3>
              <Field
                name={fieldOptionNameFromBase("frequencyPenalty")}
                type="number"
                component="input"
                className={`p-2 border rounded ${hasFieldOverride("options.frequencyPenalty") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
              <ErrorMessage
                name={fieldOptionNameFromBase("frequencyPenalty")}
                component="p"
                className="text-red-500 text-sm mt-1"
              />
            </label>
            <label className="block">
              <h3 className="text-lg font-semibold mb-2">Presence Penalty</h3>
              <Field
                name={fieldOptionNameFromBase("presencePenalty")}
                type="number"
                component="input"
                className={`p-2 border rounded ${hasFieldOverride("options.presencePenalty") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
              <ErrorMessage
                name={fieldOptionNameFromBase("presencePenalty")}
                component="p"
                className="text-red-500 text-sm mt-1"
              />
            </label>
            <label className="block mb-4">
              <h3 className="text-lg font-semibold mb-2">Stop</h3>
              <Field
                name={fieldOptionNameFromBase("stop")}
                component="input"
                className={`p-2 border rounded ${hasFieldOverride("options.stop") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
            </label>
            <label className="block mb-4">
              <h3 className="text-lg font-semibold mb-2">Number of Generations</h3>

              <Field name={fieldOptionNameFromBase("numGenerations")}>
                {({ field, form }) => {
                  if (inferenceBlocks.length > 1) {
                    // TODO this doesn't work for branches
                    const displayValue = typeof field.value === "object" && field.value?.type === "variable" ? `{{${field.value.value}}}` : field.value;
                    return <p className="text-sm">{displayValue} (multi-stage)</p>;
                  }

                  // Determine if we're in variable mode
                  const isVariableMode = typeof field.value === "object" && field.value?.type === "variable";
                  const currentValue = isVariableMode ? field.value.value : field.value;

                  const handleModeToggle = () => {
                    if (isVariableMode) {
                      // Switch to static mode
                      form.setFieldValue(field.name, 1);
                    } else {
                      // Switch to variable mode
                      form.setFieldValue(field.name, { type: "variable", value: "" });
                    }
                  };

                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {isVariableMode ? (
                          <select
                            value={currentValue || ""}
                            onChange={(e) => {
                              form.setFieldValue(field.name, { type: "variable", value: e.target.value });
                            }}
                            className={`w-full p-2 border rounded ${hasFieldOverride("options.numGenerations") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
                          >
                            <option value="">Select variable</option>
                            {availableVariables.map((variable) => (
                              <option
                                key={variable}
                                value={variable}
                              >
                                {variable}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            onChange={(e) => {
                              form.setFieldValue(field.name, parseInt(e.target.value));
                            }}
                            value={currentValue || 1}
                            className={`w-full p-2 border rounded ${hasFieldOverride("options.numGenerations") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
                          >
                            {numGenerationOptions.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                        <SudoButton
                          variant="tertiary"
                          type="button"
                          onClick={handleModeToggle}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {isVariableMode ? "Use static value" : "Use variable"}
                        </SudoButton>
                      </div>
                      {isVariableMode && !currentValue && <p className="text-sm text-red-500">Please select a variable</p>}
                    </div>
                  );
                }}
              </Field>
            </label>
            <label className="block mb-4">
              <h3 className="text-lg font-semibold mb-2">Maximum Output Tokens</h3>
              <Field
                name={fieldOptionNameFromBase("maxTokens")}
                type="number"
                className={`p-2 border rounded ${hasFieldOverride("options.maxTokens") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
              <ErrorMessage
                name={fieldOptionNameFromBase("maxTokens")}
                component="p"
                className="text-red-500 text-sm mt-1"
              />
            </label>
            <label className="block mb-4">
              <h3 className="text-lg font-semibold mb-2">Stream</h3>
              <Field
                name={fieldOptionNameFromBase("stream")}
                type="checkbox"
                className={`p-2 border rounded ${hasFieldOverride("options.maxTokens") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
              />
              <ErrorMessage
                name={fieldOptionNameFromBase("stream")}
                component="p"
                className="text-red-500 text-sm mt-1"
              />
            </label>
          </div>
        </>
      )}
      {!settingsOpen && hasOptionsError && !hasPromptError && <p className="text-red-500 text-sm">There are errors with your AI settings. Expand to see more details.</p>}
      {hasLaterInferenceBlocks && ( // TODO fix for branching
        <div>
          <label className="block mb-4">
            <h3 className="text-lg font-semibold mb-2">Output Name</h3>
            <p className="text-sm text-gray-600 mb-2">The name that will be used to reference this prompt's output in later prompts.</p>
            <Field name={fieldOptionNameFromBase("outputName")}>
              {({ field }) => (
                <input
                  {...field}
                  type="text"
                  value={field.value || ""}
                  className={`p-2 border rounded ${hasFieldOverride("options.outputName") ? "border-app-primary-cta border-2 bg-blue-50" : "border-gray-300"}`}
                />
              )}
            </Field>
            <ErrorMessage
              name={fieldOptionNameFromBase("outputName")}
              component="p"
              className="text-red-500 text-sm mt-1"
            />
          </label>
        </div>
      )}
    </>
  );
};
